import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ProjectType, ProjectType_Entity } from "../../entities/projectType";
import { toScaledInteger } from "../../helpers/numberHelpers";
import {
  formOpenSelector,
  selectedProjectTypeID,
  setFormOpen,
  setSelectedProjectTypeID,
} from "../../redux/filmsReducer";
import {
  useCreateProjectTypeMutation,
  useDeleteProjectTypeMutation,
  useUpdateProjectTypeMutation,
} from "../../redux/projectType/projectTypeEndpoints";
import {
  useProjectType,
  useProjectTypes,
} from "../../redux/projectType/projectTypeHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";
import { Check } from "@mui/icons-material";
import { currencyFormatter } from "../../constants";
import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";
import { useCreateScaleMutation } from "../../redux/scale/scaleEndpoints";
import { create } from "domain";

/**
 *
 * @returns {ReactElement} FormProjectType page
 */
export function FormProjectType() {
  const projectTypeID = useSelector(selectedProjectTypeID);
  const { projectType, isLoading } = useProjectType(projectTypeID);
  const open = useSelector(formOpenSelector("projectType"));
  const [_projectType, setProjectType] = useState<Partial<ProjectType_Entity>>(
    {}
  );

  const [createProjectType, { isLoading: isCreating }] =
    useCreateProjectTypeMutation();
  const [updateProjectType, { isLoading: isUpdating }] =
    useUpdateProjectTypeMutation();
  const [deleteProjectType] = useDeleteProjectTypeMutation();
  const askQuestion = useAskQuestion();
  const [minimumCopyistFocused, setMinimumCopyistFocused] = useState(false);
  const [minimumCopyistValue, setMinimumCopyistValue] = useState("");

  const { projectTypes } = useProjectTypes();
  const { taskTypes } = useTaskTypes();
  const { jobCategories } = useJobCategories();

  const [createScale] = useCreateScaleMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (projectType?.id) {
      setProjectType(projectType);
      setMinimumCopyistValue(
        ((projectType.minimumCopyist ?? 0) / 10000).toString()
      );
    }
  }, [projectType]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "projectType" }));
    dispatch(setSelectedProjectTypeID(undefined));
  };

  const save = () => {
    if (projectTypeID && _projectType) {
      updateProjectType({
        id: projectTypeID,
        body: {
          ..._projectType,
          minimumCopyist: toScaledInteger(minimumCopyistValue, 10000),
        },
      });
    } else if (_projectType) {
      createProjectType({
        ..._projectType,
        minimumCopyist: toScaledInteger(minimumCopyistValue, 10000),
      })
        .unwrap()
        .then((e) => {
          askQuestion("Do you want to create the scales?", ["No", "Yes"]).then(
            (i) => {
              if (i == 1) createScales(e);
            }
          );
          dispatch(setSelectedProjectTypeID(e.id));
        });
    }
    onClose();
  };

  const createScales = (pt: ProjectType_Entity) => {
    jobCategories?.forEach((jc) => {
      const tt = taskTypes?.filter((e) => e.jobCategoryID === jc.id);

      tt?.forEach((t) => {
        createScale({
          projectTypeID: pt.id,
          taskTypeID: t.id,
          rate: 0,
        });
      });
    });
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Project Type. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (projectTypeID) deleteProjectType(projectTypeID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 520, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-tag" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {projectTypeID ? "Edit" : "Create"} Project Type
        </Typography>
        <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
                alignItems: "start",
              }}
            >
              <Input
                startDecorator="Name:"
                fullWidth
                value={_projectType.name ?? ""}
                onChange={(e) =>
                  setProjectType((v) => ({ ...v, name: e.target.value }))
                }
                type="text"
                autoFocus
              />
              <Input
                fullWidth
                startDecorator="Min pay for Copyists:"
                value={
                  minimumCopyistFocused
                    ? minimumCopyistValue
                    : currencyFormatter.format(Number(minimumCopyistValue) ?? 0)
                }
                onFocus={() => setMinimumCopyistFocused(true)}
                onBlur={() => {
                  setMinimumCopyistFocused(false);
                }}
                onChange={(e) => setMinimumCopyistValue(e.target.value)}
              />
              <Checkbox
                label="Flex Plan"
                checked={_projectType.flexPlan ?? false}
                onChange={(e) => {
                  setProjectType((v) => ({
                    ...v,
                    flexPlan: e.target.checked,
                  }));
                }}
              />
            </Box>
          </Box>
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {projectTypeID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {projectTypeID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
